import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import Upload from '@/assets/icons/Upload.svg?react';
import Camera from '@/assets/icons/Camera.svg?react';
import AddReceipt from '@/assets/icons/Receipt_Add.svg?react';
import Close from '@/assets/icons/Close.svg?react';
import Receipts from '@/assets/icons/receipts.svg?react';
import { cn, getDayTimePeriod } from '@/lib/utils';
import { cva } from 'class-variance-authority';
import { Link } from 'wouter';
import { useAddReceiptContext } from '@/providers/AddReceiptContext';

interface AddReceiptEnrichButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  showReceiptsButton?: boolean;
}
export function AddReceiptEnrichButton(props: AddReceiptEnrichButtonProps) {
  const { showReceiptsButton = false, ...buttonProps } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const timePeriod = useMemo(getDayTimePeriod, []);
  const { onCameraClick, onUploadClick } = useAddReceiptContext();

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className={cn(
        'animate-in slide-in-from-bottom fill-mode-forwards fixed right-5 bottom-5 flex max-w-[450px] flex-row-reverse justify-between gap-4 duration-500 ease-out',
        {
          'w-full': isExpanded,
          'w-[90%]': !isExpanded,
        },
      )}
    >
      <div
        className={cn(
          addReceiptsButtonContainerGradientVariants({ timePeriod }),
          'relative inline-block',
        )}
      >
        <Button
          {...buttonProps}
          onClick={handleToggle}
          className={cn(
            'text-foreground flex h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border-none bg-white py-0! text-sm leading-none font-bold transition-all duration-300 ease-in-out hover:bg-white [&_svg]:size-auto',
            {
              'w-72': isExpanded,
              'w-40': !isExpanded,
            },
          )}
        >
          {!isExpanded && (
            <>
              <AddReceipt />
              Add receipt
            </>
          )}
        </Button>
        <div
          className={cn(
            `absolute top-0 left-0 flex h-full w-full items-center justify-between overflow-hidden px-4 transition-all duration-300 ease-in-out`,
            {
              'opacity-100': isExpanded,
              'pointer-events-none opacity-0': !isExpanded,
            },
          )}
        >
          <Button
            variant="ghost"
            className="h-full w-1/2 rounded-r-none px-2 hover:bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              onCameraClick();
            }}
          >
            <Camera className="mr-2 h-4 w-4" />
            Camera
          </Button>
          <Button
            variant="ghost"
            className="h-full w-1/2 rounded-l-none px-2 hover:bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              onUploadClick();
            }}
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload
          </Button>
          <Button onClick={handleToggle} variant="ghost" size="icon" className="[&_svg]:size-4">
            <Close />
          </Button>
        </div>
      </div>

      {showReceiptsButton && (
        <Link href="/receipts">
          <Button className={cn(receiptsButtonVariants({ timePeriod }))}>
            <Receipts />
            Receipts
          </Button>
        </Link>
      )}
    </div>
  );
}

const addReceiptsButtonContainerGradientVariants = cva('bg-gradient-to-r rounded-lg p-1', {
  variants: {
    timePeriod: {
      morning: 'from-[#0CA5A2] to-[#5CD7A3] shadow-[0px_1px_7px_1px_rgba(239,167,130,0.40)]',
      afternoon: 'from-[#FF9089] to-[#F6BB9E] shadow-[0px_1px_7px_4px_rgba(16,150,147,0.4)]',
      evening: 'from-[#FF9089] to-[#F6BB9E] shadow-[0px_1px_7px_4px_rgba(16,150,147,0.4)]',
    },
  },
});

const receiptsButtonVariants = cva(
  'flex items-center justify-center gap-2 rounded-lg border-4 bg-white [&_svg]:size-auto font-poppins text-sm font-bold leading-none text-foreground w-40 hover:bg-white cursor-pointer',
  {
    variants: {
      timePeriod: {
        morning: 'border-[rgba(239,167,130,0.4)] shadow-[0px_1px_7px_4px_rgba(239,167,130,0.4)]',
        afternoon: 'border-[rgba(7,165,162,0.6)] shadow-[0px_1px_7px_4px_rgba(16,150,147,0.4)]',
        evening: 'border-[rgba(7,165,162,0.6)] shadow-[0px_1px_7px_4px_rgba(16,150,147,0.4)]',
      },
    },
  },
);
